import { Injectable } from '@angular/core';
import { takeGraphQLResult } from '@new/common/operators/take-graphql-response';
import { parseError } from '@new/common/utils/error-parser';
import type { StateContext } from '@ngxs/store';
import { Action, State } from '@ngxs/store';
import { catchError, Observable, switchMap } from 'rxjs';
import { ModalService } from '../../../../common/modal/modal.service';
import { LoadExamDetailsGQL } from '../../lib/services/exam/load-exam.generated';
import { ExamCandidatesState } from '../exam-candidates/exam-candidates.state';
import { ExamQuestionGroupsState } from '../exam-question-groups/exam-question-groups.state';
import * as Act from './exam.actions';
import { examStateDefaults, ExamStateModel } from './exam.state.model';

@State<ExamStateModel>({
  name: 'exam',
  defaults: examStateDefaults,
  children: [ExamQuestionGroupsState, ExamCandidatesState]
})
@Injectable()
export class ExamState {
  constructor(
    private readonly loadExamDetailsService: LoadExamDetailsGQL,
    private readonly modalService: ModalService
  ) {}

  @Action(Act.LoadExam, { cancelUncompleted: true })
  public loadExam(
    ctx: StateContext<ExamStateModel>,
    { id }: Act.LoadExam
  ): Observable<void> {
    ctx.patchState({
      item: { ...ctx.getState().item, query: { state: 'loading' } }
    });

    return this.loadExamDetailsService.fetch({ examId: id }).pipe(
      takeGraphQLResult(),
      switchMap(res => ctx.dispatch(new Act.LoadExamSuccess(res.exam))),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.LoadExamFailure(parseError(err), id))
      )
    );
  }

  @Action(Act.LoadExamSuccess)
  public loadExamSuccess(
    ctx: StateContext<ExamStateModel>,
    { exam }: Act.LoadExamSuccess
  ): void {
    ctx.patchState({
      item: {
        ...ctx.getState().item,
        data: exam,
        query: { state: 'success' }
      }
    });
  }

  @Action(Act.LoadExamFailure)
  public loadExamFailure(
    ctx: StateContext<ExamStateModel>,
    { error, id }: Act.LoadExamFailure
  ): void {
    ctx.patchState({
      item: { ...ctx.getState().item, query: { state: 'failure', error } }
    });

    this.modalService.error({ errorCode: error.code }).then(retry => {
      if (!retry) return;
      ctx.dispatch(new Act.LoadExam(id));
    });
  }
}
